import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../../components/navbar/navbar';
import Sidebar from '../../components/sidebar/sidebar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

// Servicios y URLs
import {
    API_URLS,
    obtenerProductos,
    obtenerEntradas,
    obtenerSalidas,
    obtenerMantenimientoss
} from '../../Services/servicioURL';

// Interfaces existentes
interface InventarioItem {
    cve_producto: string;
    descripcion: string;
    modelo: string;
    entradaTotal: number;
    salidaTotal: number;
    mantenimientoTotal: number;
    existencia: number;
    fechaUltimaEntrada?: string;
    // NUEVO: Agregamos campos para línea y sublínea
    cve_linea?: string;
    nombre_linea?: string;
    cve_sublinea?: string;
    nombre_sublinea?: string;
}

interface EntradaDetalle {
    cve_producto: string;
    cantidad: number;
}

interface Entrada {
    folio: string;
    fecha: string;
    entradaDetalle: EntradaDetalle[];
}

interface SalidaDetalle {
    cve_producto: string;
    cantidad: number;
}

interface Salida {
    folio: string;
    salidaDetalle: SalidaDetalle[];
}

interface MantenimientoDetalle {
    cve_producto: string;
    cantidad: number;
}

interface Mantenimiento {
    folio: string;
    mantenimientoDetalle: MantenimientoDetalle[];
}

// NUEVAS INTERFACES para líneas y sublíneas
interface Linea {
    cve: string;
    descripcion: string;
    nombre: string;
}

interface Sublinea {
    cve: string;
    descripcion: string;
    cve_linea: string;
    nombre: string;
}

// Tipos de filtros
type FiltroTipo = 'todos' | 'con-existencia' | 'sin-existencia' | 'con-entradas' | 'sin-entradas';

const Inventario: React.FC = () => {
    const [searchParams] = useSearchParams();
    const [inventario, setInventario] = useState<InventarioItem[]>([]);
    const [filteredInventario, setFilteredInventario] = useState<InventarioItem[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [filtroActivo, setFiltroActivo] = useState<FiltroTipo>('todos');
    const [isMobile, setIsMobile] = useState<boolean>(false);
    
    // Estados para el ordenamiento
    const [sortField, setSortField] = useState<string>('');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(null);

    // NUEVOS ESTADOS para líneas y sublíneas
    const [lineas, setLineas] = useState<Linea[]>([]);
    const [sublineas, setSublineas] = useState<Sublinea[]>([]);
    const [lineaSeleccionada, setLineaSeleccionada] = useState<string>('');
    const [sublineaSeleccionada, setSublineaSeleccionada] = useState<string>('');
    const [sublineasFiltradas, setSublineasFiltradas] = useState<Sublinea[]>([]);
    const [loadingLineas, setLoadingLineas] = useState<boolean>(false);

    // Detectar si es celular
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1100);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // NUEVA FUNCIÓN: Cargar líneas desde la API
    const cargarLineas = async () => {
        try {
            setLoadingLineas(true);
            const response = await fetch(API_URLS.lineas.obtener);
            if (!response.ok) throw new Error('Error al obtener líneas');
            const lineasData = await response.json();
            setLineas(lineasData);
        } catch (error) {
            console.error('Error al cargar líneas:', error);
        } finally {
            setLoadingLineas(false);
        }
    };

    // NUEVA FUNCIÓN: Cargar sublíneas desde la API
    const cargarSublineas = async () => {
        try {
            const response = await fetch(API_URLS.Sublineas.obtener);
            if (!response.ok) throw new Error('Error al obtener sublíneas');
            const sublineasData = await response.json();
            setSublineas(sublineasData);
        } catch (error) {
            console.error('Error al cargar sublíneas:', error);
        }
    };

    // NUEVA FUNCIÓN: Filtrar sublíneas por línea seleccionada
    const filtrarSublineasPorLinea = (cveLinea: string) => {
        if (!cveLinea) {
            setSublineasFiltradas([]);
            return;
        }
        const sublineasDeLaLinea = sublineas.filter(sub => sub.cve_linea === cveLinea);
        setSublineasFiltradas(sublineasDeLaLinea);
    };

    // NUEVA FUNCIÓN: Manejar cambio de línea
    const handleLineaChange = (cveLinea: string) => {
        setLineaSeleccionada(cveLinea);
        setSublineaSeleccionada(''); // Resetear sublínea cuando cambia la línea
        filtrarSublineasPorLinea(cveLinea);
    };

    // NUEVA FUNCIÓN: Limpiar filtros de línea y sublínea
    const limpiarFiltrosLineaSublinea = () => {
        setLineaSeleccionada('');
        setSublineaSeleccionada('');
        setSublineasFiltradas([]);
    };

    // Configuración de filtros (sin cambios)
    const filtrosConfig = {
        'todos': {
            label: 'Todos',
            labelComplete: 'Todos los productos',
            icon: 'pi pi-list',
            color: 'primary',
            descripcion: 'Mostrar todos los productos del catálogo',
            emoji: '📦'
        },
        'con-existencia': {
            label: 'Con stock',
            labelComplete: 'Con existencia',
            icon: 'pi pi-check-circle',
            color: 'success',
            descripcion: 'Productos con cantidad disponible',
            emoji: '✅'
        },
        'sin-existencia': {
            label: 'Sin stock',
            labelComplete: 'Sin existencia',
            icon: 'pi pi-times-circle',
            color: 'danger',
            descripcion: 'Productos agotados',
            emoji: '❌'
        },
        'con-entradas': {
            label: 'Con entradas',
            labelComplete: 'Con entradas',
            icon: 'pi pi-arrow-up',
            color: 'info',
            descripcion: 'Productos que han tenido entradas',
            emoji: '📈'
        },
        'sin-entradas': {
            label: 'Sin entradas',
            labelComplete: 'Sin entradas',
            icon: 'pi pi-minus-circle',
            color: 'warning',
            descripcion: 'Productos sin entradas registradas',
            emoji: '📉'
        }
    };

    // Función para obtener la fecha de la última entrada de un producto
    const obtenerFechaUltimaEntrada = (cve_producto: string, entradas: Entrada[]): string | null => {
        const entradasDelProducto = entradas.filter(entrada => 
            entrada.entradaDetalle.some(detalle => detalle.cve_producto === cve_producto)
        );
        
        if (entradasDelProducto.length === 0) return null;
        
        const entradasOrdenadas = entradasDelProducto.sort((a, b) => 
            b.fecha.localeCompare(a.fecha)
        );
        
        return entradasOrdenadas[0].fecha;
    };

    // Función para formatear fecha
    const formatearFecha = (fecha: string | null): string => {
        if (!fecha) return 'Sin entradas';
        
        try {
            const [year, month, day] = fecha.split('T')[0].split('-');
            return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
        } catch (error) {
            console.error('Error al formatear fecha:', error);
            return 'Fecha inválida';
        }
    };

    // Función para aplicar filtro por categoría
    const aplicarFiltroPorCategoria = (productos: InventarioItem[], filtro: FiltroTipo): InventarioItem[] => {
        switch (filtro) {
            case 'con-existencia':
                return productos.filter(item => item.existencia > 0);
            case 'sin-existencia':
                return productos.filter(item => item.existencia <= 0);
            case 'con-entradas':
                return productos.filter(item => item.entradaTotal > 0);
            case 'sin-entradas':
                return productos.filter(item => item.entradaTotal <= 0);
            case 'todos':
            default:
                return productos;
        }
    };

    // Función para cambiar filtro
    const cambiarFiltro = (nuevoFiltro: FiltroTipo) => {
        setFiltroActivo(nuevoFiltro);
        setSortField('');
        setSortOrder(null);
    };

    // Función para obtener estadísticas de filtros
    const obtenerEstadisticasFiltros = () => {
        return {
            todos: inventario.length,
            'con-existencia': inventario.filter(item => item.existencia > 0).length,
            'sin-existencia': inventario.filter(item => item.existencia <= 0).length,
            'con-entradas': inventario.filter(item => item.entradaTotal > 0).length,
            'sin-entradas': inventario.filter(item => item.entradaTotal <= 0).length
        };
    };

    // Función para exportar a Excel (actualizada con línea y sublínea)
    const exportToExcel = () => {
        try {
            const excelData = filteredInventario.map(item => ({
                'Clave': item.cve_producto,
                'Modelo': item.modelo || 'N/A',
                'Descripción': item.descripcion,
                'Línea': item.nombre_linea || 'N/A',
                'Sublínea': item.nombre_sublinea || 'N/A',
                'Total Entradas': item.entradaTotal,
                'Total Salidas': item.salidaTotal,
                'Total Mantenimientos': item.mantenimientoTotal,
                'Existencia': item.existencia,
                'Última Entrada': formatearFecha(item.fechaUltimaEntrada || null)
            }));

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(excelData);

            const colWidths = [
                { wch: 12 }, { wch: 15 }, { wch: 40 }, { wch: 20 }, { wch: 20 },
                { wch: 15 }, { wch: 15 }, { wch: 18 }, { wch: 12 }, { wch: 18 }
            ];
            ws['!cols'] = colWidths;

            XLSX.utils.book_append_sheet(wb, ws, 'Inventario');

            const fecha = new Date().toISOString().slice(0, 10);
            const filtroNombre = filtrosConfig[filtroActivo].label.replace(/\s+/g, '_').toLowerCase();
            const nombreArchivo = `inventario_BBT_SIS_${filtroNombre}_${fecha}.xlsx`;

            XLSX.writeFile(wb, nombreArchivo);

        } catch (error) {
            console.error('Error al generar Excel:', error);
            alert('Ocurrió un error al generar el archivo Excel. Por favor intente nuevamente.');
        }
    };

    // Función para exportar a PDF (actualizada con línea y sublínea)
    const exportToPDF = () => {
        try {
            const doc = new jsPDF('l', 'pt', 'a4');
            const img = new Image();
            img.src = '/dist/img/bbtt2.png';
            img.onload = function() {
                doc.addImage(img, 'PNG', 40, 20, 65, 65);
                doc.setProperties({
                    title: 'Reporte de Inventario BBT-SIS',
                    subject: 'Inventario actual',
                    author: 'Sistema de Inventarios',
                    keywords: 'inventario, reporte, pdf',
                    creator: 'Sistema de Inventarios'
                });

                doc.setFontSize(18);
                doc.setTextColor(40);
                doc.text('Reporte de Inventario BBT-SIS', 150, 40);
                doc.setFontSize(10);
                doc.setTextColor(100);
                doc.text(`Generado el: ${new Date().toLocaleDateString('es-ES')}`, 150, 60);
                doc.text(`Filtro aplicado: ${filtrosConfig[filtroActivo].labelComplete}`, 150, 75);
                doc.text(`Total de productos: ${filteredInventario.length}`, 150, 90);

                const tableData = filteredInventario.map(item => [
                    item.cve_producto,
                    item.modelo || 'N/A',
                    item.descripcion,
                    item.nombre_linea || 'N/A',
                    item.nombre_sublinea || 'N/A',
                    item.entradaTotal,
                    item.salidaTotal,
                    item.mantenimientoTotal,
                    item.existencia,
                    formatearFecha(item.fechaUltimaEntrada || null)
                ]);

                autoTable(doc, {
                    head: [['Clave', 'Modelo', 'Descripción', 'Línea', 'Sublínea', 'Entradas', 'Salidas', 'Mantenimientos', 'Existencia', 'Última Entrada']],
                    body: tableData,
                    startY: 105,
                    margin: { horizontal: 40 },
                    styles: {
                        fontSize: 7,
                        cellPadding: 2,
                        overflow: 'linebreak',
                        valign: 'middle'
                    },
                    headStyles: {
                        fillColor: [41, 128, 185],
                        textColor: 255,
                        fontStyle: 'bold',
                        halign: 'center',
                        fontSize: 8
                    },
                    bodyStyles: {
                        halign: 'center',
                        valign: 'middle'
                    },
                    columnStyles: {
                        0: { cellWidth: 50, halign: 'center' },
                        1: { cellWidth: 60, halign: 'center' },
                        2: { cellWidth: 'auto', halign: 'left' },
                        3: { cellWidth: 60, halign: 'center' },
                        4: { cellWidth: 60, halign: 'center' },
                        5: { cellWidth: 50, halign: 'center' },
                        6: { cellWidth: 50, halign: 'center' },
                        7: { cellWidth: 60, halign: 'center' },
                        8: { cellWidth: 50, halign: 'center' },
                        9: { cellWidth: 70, halign: 'center' }
                    },
                    didDrawPage: function (data: { pageNumber: any; settings: { margin: { left: number; }; }; }) {
                        const pageCount = doc.getNumberOfPages();
                        const currentPage = data.pageNumber;
                        doc.setFontSize(10);
                        doc.setTextColor(150);
                        doc.text(
                            `Página ${currentPage} de ${pageCount}`,
                            data.settings.margin.left,
                            doc.internal.pageSize.height - 20
                        );
                    }
                });

                const filtroNombre = filtrosConfig[filtroActivo].label.replace(/\s+/g, '_').toLowerCase();
                doc.save(`inventario_${filtroNombre}_${new Date().toISOString().slice(0, 10)}.pdf`);
            };
            img.onerror = function() {
                console.error('Error al cargar la imagen del membretado');
                const filtroNombre = filtrosConfig[filtroActivo].label.replace(/\s+/g, '_').toLowerCase();
                doc.save(`inventario_${filtroNombre}_${new Date().toISOString().slice(0, 10)}.pdf`);
            };
        } catch (error) {
            console.error('Error al generar PDF:', error);
            alert('Ocurrió un error al generar el PDF. Por favor intente nuevamente.');
        }
    };

    const sumarMovimientosPorProducto = (
        movimientos: (Entrada | Salida)[]
    ): Map<string, number> => {
        const totalMap = new Map<string, number>();

        movimientos.forEach(mov => {
            const detalles = 'entradaDetalle' in mov ? mov.entradaDetalle : mov.salidaDetalle;

            detalles.forEach(detalle => {
                const actual = totalMap.get(detalle.cve_producto) || 0;
                totalMap.set(detalle.cve_producto, actual + detalle.cantidad);
            });
        });

        return totalMap;
    };

    const sumarMantenimientosPorProducto = (mantenimientos: Mantenimiento[]): Map<string, number> => {
        const totalMap = new Map<string, number>();
        mantenimientos.forEach(mant => {
            if (Array.isArray(mant.mantenimientoDetalle)) {
                mant.mantenimientoDetalle.forEach(detalle => {
                    const actual = totalMap.get(detalle.cve_producto) || 0;
                    totalMap.set(detalle.cve_producto, actual + detalle.cantidad);
                });
            }
        });
        return totalMap;
    };

    // Función para manejar el ordenamiento manual
    const handleSort = (field: string) => {
        let newSortOrder: 'asc' | 'desc' | null = 'asc';
        
        if (sortField === field) {
            if (sortOrder === 'asc') {
                newSortOrder = 'desc';
            } else if (sortOrder === 'desc') {
                newSortOrder = null;
            } else {
                newSortOrder = 'asc';
            }
        }
        
        setSortField(field);
        setSortOrder(newSortOrder);
        
        if (newSortOrder === null) {
            applyFilters();
        } else {
            const sorted = [...filteredInventario].sort((a, b) => {
                let aValue = a[field as keyof InventarioItem];
                let bValue = b[field as keyof InventarioItem];
                
                if (aValue === null || aValue === undefined) aValue = '';
                if (bValue === null || bValue === undefined) bValue = '';
                
                const aStr = String(aValue).toLowerCase();
                const bStr = String(bValue).toLowerCase();
                
                if (newSortOrder === 'asc') {
                    return aStr.localeCompare(bStr, undefined, { numeric: true });
                } else {
                    return bStr.localeCompare(aStr, undefined, { numeric: true });
                }
            });
            
            setFilteredInventario(sorted);
        }
    };

    const resetSort = () => {
        setSortField('');
        setSortOrder(null);
        applyFilters();
    };

    // FUNCIÓN ACTUALIZADA: Aplicar filtros incluyendo línea y sublínea
    const applyFilters = () => {
        let filtered = inventario;
        
        // Aplicar filtro por categoría
        filtered = aplicarFiltroPorCategoria(filtered, filtroActivo);
        
        // NUEVO: Aplicar filtro por línea
        if (lineaSeleccionada) {
            filtered = filtered.filter(item => item.cve_linea === lineaSeleccionada);
        }
        
        // NUEVO: Aplicar filtro por sublínea
        if (sublineaSeleccionada) {
            filtered = filtered.filter(item => item.cve_sublinea === sublineaSeleccionada);
        }
        
        // Aplicar filtro de búsqueda por texto
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(item =>
                item.descripcion.toLowerCase().includes(term) ||
                item.cve_producto.toLowerCase().includes(term) ||
                item.modelo.toLowerCase().includes(term) ||
                (item.nombre_linea && item.nombre_linea.toLowerCase().includes(term)) ||
                (item.nombre_sublinea && item.nombre_sublinea.toLowerCase().includes(term))
            );
        }
        
        setFilteredInventario(filtered);
    };

    const getSortFieldName = (field: string): string => {
        const fieldNames: { [key: string]: string } = {
            'cve_producto': 'Clave',
            'modelo': 'Modelo',
            'descripcion': 'Descripción',
            'nombre_linea': 'Línea',
            'nombre_sublinea': 'Sublínea',
            'entradaTotal': 'Entradas',
            'salidaTotal': 'Salidas',
            'mantenimientoTotal': 'Mantenimientos',
            'existencia': 'Existencia',
            'fechaUltimaEntrada': 'Última Entrada'
        };
        return fieldNames[field] || field;
    };

    const renderSortableHeader = (field: string, label: string) => {
        const isActive = sortField === field;
        const icon = isActive 
            ? (sortOrder === 'asc' ? 'pi pi-sort-up' : sortOrder === 'desc' ? 'pi pi-sort-down' : 'pi pi-sort')
            : 'pi pi-sort';
        
        return (
            <div 
                style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    cursor: 'pointer',
                    userSelect: 'none',
                    gap: '0.5rem',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s',
                    fontWeight: '600',
                    fontSize: isMobile ? '0.8rem' : '0.9rem'
                }}
                onClick={() => handleSort(field)}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 123, 255, 0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
                <span>{label}</span>
                <i className={icon} style={{ 
                    color: isActive ? '#007bff' : '#6c757d',
                    fontSize: '0.8rem'
                }}></i>
            </div>
        );
    };

    // Renderizar vista de tarjetas para móvil (actualizada con línea y sublínea)
    const renderMobileCards = () => {
        if (filteredInventario.length === 0) {
            return (
                <Card style={{ textAlign: 'center', padding: '2rem' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
                    <h3>No hay productos</h3>
                    <p>No hay productos que coincidan con los filtros aplicados</p>
                </Card>
            );
        }

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {filteredInventario.map((item, index) => (
                    <Card key={index} style={{ 
                        padding: '1rem',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#374151', marginBottom: '0.25rem' }}>
                                    🔑 {item.cve_producto}
                                </div>
                                {item.modelo && (
                                    <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                                        🏷️ {item.modelo}
                                    </div>
                                )}
                                <div style={{ fontSize: '0.9rem', color: '#374151', marginBottom: '0.5rem' }}>
                                    {item.descripcion}
                                </div>
                                {/* NUEVO: Mostrar línea y sublínea en cards móviles */}
                                {(item.nombre_linea || item.nombre_sublinea) && (
                                    <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                                        {item.nombre_linea && <span>📂 {item.nombre_linea}</span>}
                                        {item.nombre_linea && item.nombre_sublinea && <span> • </span>}
                                        {item.nombre_sublinea && <span>📁 {item.nombre_sublinea}</span>}
                                    </div>
                                )}
                            </div>
                            <Badge 
                                value={item.existencia}
                                severity={
                                    item.existencia <= 0 ? 'danger' : 
                                    item.existencia <= 5 ? 'warning' : 'success'
                                }
                                style={{ fontSize: '1rem', fontWeight: 'bold' }}
                            />
                        </div>
                        
                        <Divider style={{ margin: '0.5rem 0' }} />
                        
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.85rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>📈 Entradas:</span>
                                <Badge value={item.entradaTotal} severity={item.entradaTotal > 0 ? 'success' : 'secondary'} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>📉 Salidas:</span>
                                <Badge value={item.salidaTotal} severity={item.salidaTotal > 0 ? 'warning' : 'secondary'} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>🔧 Mantenimientos:</span>
                                <Badge value={item.mantenimientoTotal} severity={item.mantenimientoTotal > 0 ? 'info' : 'secondary'} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>📅 Última entrada:</span>
                                <span style={{ 
                                    color: item.fechaUltimaEntrada ? '#059669' : '#6b7280',
                                    fontWeight: item.fechaUltimaEntrada ? '600' : 'normal',
                                    fontSize: '0.8rem'
                                }}>
                                    {formatearFecha(item.fechaUltimaEntrada || null)}
                                </span>
                            </div>
                        </div>
                    </Card>
                ))}
            </div>
        );
    };

    // Capturar parámetros de query al cargar el componente
    useEffect(() => {
        const filtroParam = searchParams.get('filtro') as FiltroTipo;
        if (filtroParam && filtrosConfig[filtroParam]) {
            setFiltroActivo(filtroParam);
        }
    }, [searchParams]);

    // NUEVO: Cargar líneas y sublíneas al montar el componente
    useEffect(() => {
        cargarLineas();
        cargarSublineas();
    }, []);

    // NUEVO: Efecto para filtrar sublíneas cuando cambia la línea seleccionada
    useEffect(() => {
        filtrarSublineasPorLinea(lineaSeleccionada);
    }, [lineaSeleccionada, sublineas]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const productos = await obtenerProductos(API_URLS.productos.obtener);
                const entradas = await obtenerEntradas(API_URLS.entradas.obtener);
                const salidas = await obtenerSalidas(API_URLS.salidas.obtener);
                const mantenimientos = await obtenerMantenimientoss(API_URLS.mantenimientos.obtener);

                const entradasTotales = sumarMovimientosPorProducto(entradas);
                const salidasTotales = sumarMovimientosPorProducto(salidas);
                const mantenimientosTotales = sumarMantenimientosPorProducto(mantenimientos);

                const inventarioFinal = productos.map((producto: any) => {
                    const entradaTotal = entradasTotales.get(producto.cve) || 0;
                    const salidaTotal = salidasTotales.get(producto.cve) || 0;
                    const mantenimientoTotal = mantenimientosTotales.get(producto.cve) || 0;
                    const fechaUltimaEntrada = obtenerFechaUltimaEntrada(producto.cve, entradas);

                    return {
                        cve_producto: producto.cve,
                        descripcion: producto.descripcion,
                        modelo: producto.modelo,
                        // NUEVO: Agregar información de línea y sublínea
                        cve_linea: producto.cve_linea,
                        nombre_linea: producto.linea?.descripcion,
                        cve_sublinea: producto.cve_sublinea,
                        nombre_sublinea: producto.sublinea?.descripcion,
                        entradaTotal,
                        salidaTotal,
                        mantenimientoTotal,
                        existencia: entradaTotal - salidaTotal - mantenimientoTotal,
                        fechaUltimaEntrada
                    };
                });

                setInventario(inventarioFinal);
                setFilteredInventario(inventarioFinal);

            } catch (error) {
                console.error('Error al cargar inventario:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // EFECTO ACTUALIZADO: Aplicar filtros cuando cambian los criterios
    useEffect(() => {
        applyFilters();
        setSortField('');
        setSortOrder(null);
    }, [searchTerm, inventario, filtroActivo, lineaSeleccionada, sublineaSeleccionada]);

    const estadisticas = obtenerEstadisticasFiltros();

    return (
        <>
            <Navbar />
            <Sidebar />

            <div className="content-wrapper" style={{
                backgroundImage: 'url(/dist/img/FondoLogin.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: isMobile ? 'scroll' : 'fixed',
                minHeight: '100vh',
                position: 'relative',
                padding: isMobile ? '10px' : '20px'
            }}>
                
                {/* Header principal */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: isMobile ? '12px' : '16px',
                    padding: isMobile ? '16px' : '24px',
                    marginBottom: isMobile ? '15px' : '20px',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        marginBottom: '16px',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: isMobile ? '12px' : '0'
                    }}>
                        <h1 style={{ 
                            margin: 0, 
                            color: '#1f2937', 
                            fontSize: isMobile ? '1.5rem' : '2rem', 
                            fontWeight: '700',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            textAlign: isMobile ? 'center' : 'left'
                        }}>
                            📊 Inventario General
                        </h1>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 16px',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            borderRadius: '20px',
                            fontSize: '0.85rem',
                            fontWeight: '600'
                        }}>
                            <i className="pi pi-chart-bar"></i>
                            {loading ? 'CARGANDO...' : `${filteredInventario.length} PRODUCTOS`}
                        </div>
                    </div>
                    <div style={{ height: '2px', background: 'linear-gradient(90deg, #3b82f6, #10b981)', borderRadius: '1px' }}></div>
                </div>

                {/* NUEVA SECCIÓN: Filtros por línea y sublínea */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px',
                    padding: isMobile ? '16px' : '24px',
                    marginBottom: isMobile ? '15px' : '20px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <h3 style={{ 
                        margin: '0 0 20px 0', 
                        color: '#374151', 
                        fontSize: isMobile ? '1.1rem' : '1.3rem', 
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        📂 Filtros por Línea y Sublínea
                    </h3>

                    <div style={{ 
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr auto',
                        gap: '16px',
                        alignItems: 'end'
                    }}>
                        {/* Dropdown de Líneas */}
                        <div>
                            <label style={{ 
                                display: 'block', 
                                marginBottom: '8px', 
                                fontWeight: '600', 
                                color: '#374151',
                                fontSize: '0.9rem'
                            }}>
                                📂 Línea:
                            </label>
                            <Dropdown
                                value={lineaSeleccionada}
                                onChange={(e) => handleLineaChange(e.value)}
                                options={lineas}
                                optionLabel="nombre"
                                optionValue="cve"
                                placeholder="Seleccionar línea..."
                                filter
                                showClear
                                disabled={loadingLineas}
                                style={{ 
                                    width: '100%',
                                    minHeight: '40px'
                                }}
                                panelStyle={{ maxHeight: '200px' }}
                            />
                        </div>

                        {/* Dropdown de Sublíneas */}
                        <div>
                            <label style={{ 
                                display: 'block', 
                                marginBottom: '8px', 
                                fontWeight: '600', 
                                color: '#374151',
                                fontSize: '0.9rem'
                            }}>
                                📁 Sublínea:
                            </label>
                            <Dropdown
                                value={sublineaSeleccionada}
                                onChange={(e) => setSublineaSeleccionada(e.value)}
                                options={sublineasFiltradas}
                                optionLabel="nombre"
                                optionValue="cve"
                                placeholder={lineaSeleccionada ? "Seleccionar sublínea..." : "Primero selecciona una línea"}
                                filter
                                showClear
                                disabled={!lineaSeleccionada || sublineasFiltradas.length === 0}
                                style={{ 
                                    width: '100%',
                                    minHeight: '40px'
                                }}
                                panelStyle={{ maxHeight: '200px' }}
                            />
                        </div>

                        {/* Botón para limpiar filtros */}
                        <div>
                            <Button
                                icon="pi pi-times"
                                label={isMobile ? "Limpiar" : "Limpiar filtros"}
                                severity="secondary"
                                outlined
                                onClick={limpiarFiltrosLineaSublinea}
                                disabled={!lineaSeleccionada && !sublineaSeleccionada}
                                style={{ 
                                    height: '40px',
                                    fontSize: '0.9rem',
                                    fontWeight: '600',
                                    borderRadius: '8px'
                                }}
                                tooltip="Limpiar filtros de línea y sublínea"
                            />
                        </div>
                    </div>

                    {/* Indicadores de filtros activos */}
                    {(lineaSeleccionada || sublineaSeleccionada) && (
                        <div style={{ 
                            marginTop: '16px',
                            padding: '12px 16px',
                            backgroundColor: '#f0f9ff',
                            borderRadius: '8px',
                            border: '1px solid #bae6fd',
                            fontSize: '0.9rem',
                            color: '#0c4a6e'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                <i className="pi pi-filter" style={{ color: '#0284c7' }}></i>
                                <span style={{ fontWeight: '600' }}>Filtros activos:</span>
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {lineaSeleccionada && (
                                    <Badge 
                                        value={`📂 ${lineas.find(l => l.cve === lineaSeleccionada)?.nombre || lineaSeleccionada}`}
                                        severity="info"
                                    />
                                )}
                                {sublineaSeleccionada && (
                                    <Badge 
                                        value={`📁 ${sublineasFiltradas.find(s => s.cve === sublineaSeleccionada)?.nombre || sublineaSeleccionada}`}
                                        severity="info"
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Sección de filtros existente */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px',
                    padding: isMobile ? '16px' : '24px',
                    marginBottom: isMobile ? '15px' : '20px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <h3 style={{ 
                        margin: '0 0 20px 0', 
                        color: '#374151', 
                        fontSize: isMobile ? '1.1rem' : '1.3rem', 
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        🔍 Filtros de Inventario
                    </h3>

                    {/* Botones de filtro */}
                    <div style={{ 
                        display: 'grid',
                        gridTemplateColumns: isMobile ? 'repeat(auto-fit, minmax(120px, 1fr))' : 'repeat(auto-fit, minmax(180px, 1fr))',
                        gap: isMobile ? '8px' : '12px',
                        marginBottom: '20px'
                    }}>
                        {Object.entries(filtrosConfig).map(([key, config]) => (
                            <Button
                                key={key}
                                label={isMobile ? 
                                    `${config.emoji} ${config.label} (${estadisticas[key as keyof typeof estadisticas]})` :
                                    `${config.emoji} ${config.labelComplete} (${estadisticas[key as keyof typeof estadisticas]})`
                                }
                                icon={config.icon}
                                severity={filtroActivo === key ? (config.color === 'primary' ? undefined : config.color as 'secondary' | 'success' | 'info' | 'warning' | 'danger' | 'help' | 'contrast') : 'secondary'}
                                outlined={filtroActivo !== key}
                                size="small"
                                onClick={() => cambiarFiltro(key as FiltroTipo)}
                                style={{
                                    fontWeight: filtroActivo === key ? '600' : 'normal',
                                    transform: filtroActivo === key ? 'scale(1.02)' : 'scale(1)',
                                    transition: 'all 0.2s ease',
                                    borderRadius: '8px',
                                    minHeight: '40px',
                                    fontSize: isMobile ? '0.8rem' : '0.9rem',
                                    ...(filtroActivo === key && config.color === 'primary' ? {
                                        backgroundColor: '#007bff',
                                        borderColor: '#007bff',
                                        color: 'white'
                                    } : {})
                                }}
                            />
                        ))}
                    </div>

                    {/* Información del filtro activo */}
                    {filtroActivo !== 'todos' && (
                        <div style={{ 
                            padding: '12px 16px',
                            backgroundColor: '#f0f9ff',
                            borderRadius: '8px',
                            border: '1px solid #bae6fd',
                            fontSize: isMobile ? '0.8rem' : '0.9rem',
                            color: '#0c4a6e',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            flexDirection: isMobile ? 'column' : 'row',
                            textAlign: isMobile ? 'center' : 'left'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <i className="pi pi-info-circle" style={{ color: '#0284c7' }}></i>
                                <span>
                                    <strong>{filtrosConfig[filtroActivo].emoji} Filtro activo:</strong> {filtrosConfig[filtroActivo].descripcion}
                                </span>
                            </div>
                            <Badge 
                                value={filteredInventario.length} 
                                severity="info" 
                            />
                        </div>
                    )}
                </div>

                {/* Controles de búsqueda y exportación */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px',
                    padding: isMobile ? '16px' : '20px',
                    marginBottom: isMobile ? '15px' : '20px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <div style={{ 
                        display: 'flex', 
                        gap: '16px', 
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                        flexDirection: isMobile ? 'column' : 'row'
                    }}>
                        {/* Barra de búsqueda */}
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '12px', 
                            flex: 1, 
                            width: isMobile ? '100%' : 'auto',
                            flexDirection: isMobile ? 'column' : 'row'
                        }}>
                            <div style={{ position: 'relative', flex: 1, width: isMobile ? '100%' : 'auto', maxWidth: isMobile ? 'none' : '400px' }}>
                                <i className="pi pi-search" style={{
                                    position: 'absolute',
                                    left: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#9ca3af',
                                    fontSize: '0.9rem'
                                }}></i>
                                <InputText
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="🔍 Buscar por clave, descripción, modelo, línea o sublínea..."
                                    style={{
                                        width: '100%',
                                        height: '40px',
                                        paddingLeft: '40px',
                                        border: '2px solid #e5e7eb',
                                        borderRadius: '8px',
                                        fontSize: '0.95rem'
                                    }}
                                />
                            </div>

                            {/* Indicador de ordenamiento */}
                            {sortField && sortOrder && (
                                <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '8px',
                                    padding: '8px 12px',
                                    backgroundColor: '#e0f2fe',
                                    borderRadius: '6px',
                                    border: '1px solid #b3e5fc',
                                    width: isMobile ? '100%' : 'auto'
                                }}>
                                    <i className="pi pi-sort" style={{ color: '#0277bd', fontSize: '0.9rem' }}></i>
                                    <span style={{ fontSize: '0.85rem', color: '#01579b', fontWeight: '600' }}>
                                        {getSortFieldName(sortField)} ({sortOrder === 'asc' ? '↑' : '↓'})
                                    </span>
                                    <Button
                                        icon="pi pi-times"
                                        size="small"
                                        severity="secondary"
                                        text
                                        rounded
                                        onClick={resetSort}
                                        style={{ width: '24px', height: '24px' }}
                                        tooltip="Quitar ordenamiento"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Botones de exportación */}
                        <div style={{ 
                            display: 'flex', 
                            gap: '12px',
                            width: isMobile ? '100%' : 'auto'
                        }}>
                            <Button
                                icon="pi pi-file-excel"
                                label={isMobile ? "Excel" : "Excel"}
                                severity="success"
                                onClick={exportToExcel}
                                disabled={filteredInventario.length === 0 || loading}
                                style={{ 
                                    height: '40px',
                                    fontSize: '0.9rem',
                                    fontWeight: '600',
                                    borderRadius: '8px',
                                    flex: isMobile ? 1 : 'none',
                                    minWidth: isMobile ? 'auto' : '100px'
                                }}
                                tooltip="Exportar a Excel"
                            />
                            <Button
                                icon="pi pi-file-pdf"
                                label={isMobile ? "PDF" : "PDF"}
                                severity="danger"
                                onClick={exportToPDF}
                                disabled={filteredInventario.length === 0 || loading}
                                style={{ 
                                    height: '40px',
                                    fontSize: '0.9rem',
                                    fontWeight: '600',
                                    borderRadius: '8px',
                                    flex: isMobile ? 1 : 'none',
                                    minWidth: isMobile ? 'auto' : '100px'
                                }}
                                tooltip="Exportar a PDF"
                            />
                        </div>
                    </div>
                </div>

                {/* Contenido principal - Tabla o Cards */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px',
                    padding: isMobile ? '16px' : '24px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    {isMobile ? (
                        // Vista de tarjetas para móvil
                        renderMobileCards()
                    ) : (
                        // Vista de tabla para desktop (actualizada con columnas de línea y sublínea)
                        <DataTable
                            value={filteredInventario}
                            responsiveLayout="scroll"
                            stripedRows
                            size="small"
                            emptyMessage={loading ? '🔄 Cargando inventario...' : '📭 No hay productos que coincidan con los filtros aplicados'}
                            className="p-datatable-sm"
                            style={{ 
                                fontSize: '0.9rem',
                                borderRadius: '8px',
                                overflow: 'hidden'
                            }}
                            paginator
                            rows={15}
                            rowsPerPageOptions={[10, 15, 25, 50, 100]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                            currentPageReportTemplate="📊 Mostrando {first} a {last} de {totalRecords} productos"
                            rowHover
                        >
                            <Column
                                field="cve_producto"
                                header={renderSortableHeader('cve_producto', '🔑 Clave')}
                                headerStyle={{ 
                                    fontWeight: 'bold', 
                                    textAlign: 'center',
                                    backgroundColor: '#f8fafc',
                                    padding: '12px'
                                }}
                                style={{ 
                                    width: '100px', 
                                    textAlign: 'center',
                                    fontWeight: '600',
                                    color: '#374151'
                                }}
                            />
                            <Column
                                field="modelo"
                                header={renderSortableHeader('modelo', '🏷️ Modelo')}
                                headerStyle={{ 
                                    fontWeight: 'bold', 
                                    textAlign: 'center',
                                    backgroundColor: '#f8fafc',
                                    padding: '12px'
                                }}
                                style={{ 
                                    width: '120px', 
                                    textAlign: 'center',
                                    color: '#6b7280'
                                }}
                            />
                            <Column
                                field="descripcion"
                                header={renderSortableHeader('descripcion', '📝 Descripción')}
                                headerStyle={{ 
                                    fontWeight: 'bold',
                                    backgroundColor: '#f8fafc',
                                    padding: '12px'
                                }}
                                style={{ 
                                    minWidth: '250px',
                                    color: '#374151'
                                }}
                            />
        
                            <Column
                                field="entradaTotal"
                                header={renderSortableHeader('entradaTotal', '📈 Entradas')}
                                headerStyle={{ 
                                    fontWeight: 'bold', 
                                    textAlign: 'center',
                                    backgroundColor: '#f8fafc',
                                    padding: '12px'
                                }}
                                style={{ 
                                    width: '100px', 
                                    textAlign: 'center'
                                }}
                                body={(rowData) => (
                                    <Badge 
                                        value={rowData.entradaTotal} 
                                        severity={rowData.entradaTotal > 0 ? 'success' : 'secondary'}
                                    />
                                )}
                            />
                            <Column
                                field="salidaTotal"
                                header={renderSortableHeader('salidaTotal', '📉 Salidas')}
                                headerStyle={{ 
                                    fontWeight: 'bold', 
                                    textAlign: 'center',
                                    backgroundColor: '#f8fafc',
                                    padding: '12px'
                                }}
                                style={{ 
                                    width: '100px', 
                                    textAlign: 'center'
                                }}
                                body={(rowData) => (
                                    <Badge 
                                        value={rowData.salidaTotal} 
                                        severity={rowData.salidaTotal > 0 ? 'warning' : 'secondary'}
                                    />
                                )}
                            />
                            <Column
                                field="mantenimientoTotal"
                                header={renderSortableHeader('mantenimientoTotal', '🔧 Mantenimientos')}
                                headerStyle={{ 
                                    fontWeight: 'bold', 
                                    textAlign: 'center',
                                    backgroundColor: '#f8fafc',
                                    padding: '12px'
                                }}
                                style={{ 
                                    width: '130px', 
                                    textAlign: 'center'
                                }}
                                body={(rowData) => (
                                    <Badge 
                                        value={rowData.mantenimientoTotal} 
                                        severity={rowData.mantenimientoTotal > 0 ? 'info' : 'secondary'}
                                    />
                                )}
                            />
                            <Column
                                field="existencia"
                                header={renderSortableHeader('existencia', '📦 Existencia')}
                                body={rowData => (
                                    <div style={{ textAlign: 'center' }}>
                                        <Badge 
                                            value={rowData.existencia}
                                            severity={
                                                rowData.existencia <= 0 ? 'danger' : 
                                                rowData.existencia <= 5 ? 'warning' : 'success'
                                            }
                                            style={{ 
                                                fontSize: '0.9rem',
                                                fontWeight: 'bold',
                                                minWidth: '40px'
                                            }}
                                        />
                                    </div>
                                )}
                                headerStyle={{ 
                                    width: '120px', 
                                    textAlign: 'center', 
                                    fontWeight: 'bold',
                                    backgroundColor: '#f8fafc',
                                    padding: '12px'
                                }}
                                style={{ textAlign: 'center' }}
                            />
                            <Column
                                field="fechaUltimaEntrada"
                                header={renderSortableHeader('fechaUltimaEntrada', '📅 Última Entrada')}
                                body={rowData => (
                                    <span style={{ 
                                        fontSize: '0.85rem',
                                        color: rowData.fechaUltimaEntrada ? '#059669' : '#6b7280',
                                        fontStyle: rowData.fechaUltimaEntrada ? 'normal' : 'italic',
                                        fontWeight: rowData.fechaUltimaEntrada ? '600' : 'normal'
                                    }}>
                                        {formatearFecha(rowData.fechaUltimaEntrada || null)}
                                    </span>
                                )}
                                headerStyle={{ 
                                    width: '140px', 
                                    textAlign: 'center', 
                                    fontWeight: 'bold',
                                    backgroundColor: '#f8fafc',
                                    padding: '12px'
                                }}
                                style={{ textAlign: 'center' }}
                            />
                        </DataTable>
                    )}
                </div>
            </div>

            {/* Estilos CSS adicionales para responsive */}
            <style>
                {`
                    /* Estilos responsive para DataTable */
                    @media (max-width: 768px) {
                        .p-datatable .p-datatable-thead > tr > th {
                            font-size: 0.8rem !important;
                            padding: 8px 4px !important;
                        }
                        
                        .p-datatable .p-datatable-tbody > tr > td {
                            font-size: 0.8rem !important;
                            padding: 8px 4px !important;
                        }
                        
                        .p-paginator {
                            flex-wrap: wrap;
                            gap: 0.5rem;
                        }
                        
                        .p-paginator .p-paginator-current {
                            font-size: 0.8rem;
                        }
                        
                        .content-wrapper {
                            margin-left: 0 !important;
                        }
                    }
                    
                    /* Mejoras en la tabla responsive */
                    .p-datatable-responsive-demo .p-datatable-tbody > tr > td .p-column-title {
                        display: none;
                    }
                    
                    @media screen and (max-width: 40rem) {
                        .p-datatable-responsive-demo .p-datatable-thead > tr > th,
                        .p-datatable-responsive-demo .p-datatable-tfoot > tr > td {
                            display: none !important;
                        }
                        
                        .p-datatable-responsive-demo .p-datatable-tbody > tr > td {
                            text-align: left;
                            display: block;
                            border: 0 none;
                            width: 100% !important;
                            float: left;
                            clear: left;
                        }
                        
                        .p-datatable-responsive-demo .p-datatable-tbody > tr > td .p-column-title {
                            padding: .4rem;
                            min-width: 30%;
                            display: inline-block;
                            margin: -.4rem 1rem -.4rem -.4rem;
                            font-weight: bold;
                        }
                    }
                    
                    /* Estilos para cards en móvil */
                    .mobile-card {
                        transition: transform 0.2s ease, box-shadow 0.2s ease;
                    }
                    
                    .mobile-card:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 4px 8px rgba(0,0,0,0.15);
                    }
                    
                    /* Optimizaciones para pantallas muy pequeñas */
                    @media (max-width: 480px) {
                        .content-wrapper {
                            padding: 8px !important;
                        }
                        
                        h1 {
                            font-size: 1.3rem !important;
                        }
                        
                        h3 {
                            font-size: 1rem !important;
                        }
                        
                        .p-button {
                            font-size: 0.8rem !important;
                        }
                    }

                    /* NUEVOS ESTILOS para dropdowns */
                    .p-dropdown {
                        border: 2px solid #e5e7eb;
                        border-radius: 8px;
                        transition: border-color 0.2s ease;
                    }
                    
                    .p-dropdown:not(.p-disabled):hover {
                        border-color: #d1d5db;
                    }
                    
                    .p-dropdown:not(.p-disabled).p-focus {
                        border-color: #3border-color: #3b82f6;
                        box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.25);
                    }

                    .p-dropdown-panel {
                        border: 1px solid #d1d5db;
                        border-radius: 8px;
                        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
                    }

                    .p-dropdown-item {
                        padding: 8px 12px;
                        transition: background-color 0.2s ease;
                    }

                    .p-dropdown-item:hover {
                        background-color: #f3f4f6;
                    }

                    .p-dropdown-item.p-highlight {
                        background-color: #3b82f6;
                        color: white;
                    }

                    /* Estilos para badges en filtros */
                    .p-badge {
                        font-size: 0.8rem;
                        padding: 0.25rem 0.5rem;
                        border-radius: 12px;
                        font-weight: 600;
                    }

                    /* Mejoras en la responsividad de los filtros */
                    @media (max-width: 768px) {
                        .p-dropdown {
                            font-size: 0.9rem;
                        }
                        
                        .p-dropdown .p-dropdown-label {
                            padding: 8px 12px;
                        }
                    }

                    /* Estilos para indicadores de filtros activos */
                    .filter-indicator {
                        animation: fadeIn 0.3s ease-in-out;
                    }

                    @keyframes fadeIn {
                        from {
                            opacity: 0;
                            transform: translateY(-10px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }

                    /* Estilos para mejorar la accesibilidad */
                    .p-button:focus,
                    .p-dropdown:focus,
                    .p-inputtext:focus {
                        outline: 2px solid #3b82f6;
                        outline-offset: 2px;
                    }

                    /* Estilos para el estado de carga */
                    .loading-overlay {
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background-color: rgba(255, 255, 255, 0.8);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        z-index: 1000;
                        border-radius: 12px;
                    }

                    /* Animaciones para las tarjetas móviles */
                    .mobile-card {
                        animation: slideInUp 0.3s ease-out;
                    }

                    @keyframes slideInUp {
                        from {
                            opacity: 0;
                            transform: translateY(20px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }

                    /* Estilos para tooltips personalizados */
                    .p-tooltip .p-tooltip-text {
                        background-color: #1f2937;
                        color: white;
                        border-radius: 6px;
                        padding: 8px 12px;
                        font-size: 0.85rem;
                        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                    }

                    /* Mejoras en la tabla para mejor legibilidad */
                    .p-datatable .p-datatable-tbody > tr:hover {
                        background-color: #f8fafc;
                        transition: background-color 0.2s ease;
                    }

                    .p-datatable .p-datatable-thead > tr > th {
                        background-color: #f8fafc;
                        border-bottom: 2px solid #e5e7eb;
                        font-weight: 600;
                        color: #374151;
                    }

                    /* Estilos para el paginador */
                    .p-paginator {
                        background-color: #f8fafc;
                        border: 1px solid #e5e7eb;
                        border-radius: 8px;
                        padding: 12px;
                    }

                    .p-paginator .p-paginator-pages .p-paginator-page {
                        border-radius: 6px;
                        margin: 0 2px;
                        transition: all 0.2s ease;
                    }

                    .p-paginator .p-paginator-pages .p-paginator-page:hover {
                        background-color: #e5e7eb;
                    }

                    .p-paginator .p-paginator-pages .p-paginator-page.p-highlight {
                        background-color: #3b82f6;
                        color: white;
                    }

                    /* Estilos para modo oscuro (si se implementa en el futuro) */
                    .dark-mode .p-datatable {
                        background-color: #1f2937;
                        color: #f9fafb;
                    }

                    .dark-mode .p-datatable .p-datatable-thead > tr > th {
                        background-color: #374151;
                        color: #f9fafb;
                        border-color: #4b5563;
                    }

                    .dark-mode .p-datatable .p-datatable-tbody > tr {
                        background-color: #1f2937;
                        color: #f9fafb;
                    }

                    .dark-mode .p-datatable .p-datatable-tbody > tr:hover {
                        background-color: #374151;
                    }

                    /* Optimizaciones para impresión */
                    @media print {
                        .content-wrapper {
                            background: none !important;
                            padding: 0 !important;
                        }
                        
                        .p-button,
                        .p-dropdown,
                        .p-inputtext {
                            display: none !important;
                        }
                        
                        .p-datatable {
                            font-size: 10px !important;
                        }
                        
                        .p-datatable .p-datatable-thead > tr > th,
                        .p-datatable .p-datatable-tbody > tr > td {
                            padding: 4px !important;
                            border: 1px solid #000 !important;
                        }
                    }

                    /* Estilos para pantallas de alta resolución */
                    @media (min-width: 1920px) {
                        .content-wrapper {
                            max-width: 1800px;
                            margin: 0 auto;
                        }
                        
                        .p-datatable {
                            font-size: 1rem;
                        }
                        
                        .p-datatable .p-datatable-thead > tr > th,
                        .p-datatable .p-datatable-tbody > tr > td {
                            padding: 16px;
                        }
                    }

                    /* Animaciones suaves para cambios de estado */
                    * {
                        transition: color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease;
                    }

                    /* Estilos para elementos deshabilitados */
                    .p-disabled {
                        opacity: 0.6;
                        cursor: not-allowed;
                    }

                    /* Mejoras en la accesibilidad para lectores de pantalla */
                    .sr-only {
                        position: absolute;
                        width: 1px;
                        height: 1px;
                        padding: 0;
                        margin: -1px;
                        overflow: hidden;
                        clip: rect(0, 0, 0, 0);
                        white-space: nowrap;
                        border: 0;
                    }

                    /* Estilos para indicadores de carga */
                    .loading-spinner {
                        border: 3px solid #f3f3f3;
                        border-top: 3px solid #3b82f6;
                        border-radius: 50%;
                        width: 30px;
                        height: 30px;
                        animation: spin 1s linear infinite;
                        margin: 0 auto;
                    }

                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }

                    /* Estilos para mensajes de error */
                    .error-message {
                        color: #dc2626;
                        background-color: #fef2f2;
                        border: 1px solid #fecaca;
                        border-radius: 8px;
                        padding: 12px 16px;
                        margin: 16px 0;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                    }

                    .error-message i {
                        color: #dc2626;
                        font-size: 1.1rem;
                    }

                    /* Estilos para mensajes de éxito */
                    .success-message {
                        color: #059669;
                        background-color: #f0fdf4;
                        border: 1px solid #bbf7d0;
                        border-radius: 8px;
                        padding: 12px 16px;
                        margin: 16px 0;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                    }

                    .success-message i {
                        color: #059669;
                        font-size: 1.1rem;
                    }

                    /* Estilos para el scroll personalizado */
                    ::-webkit-scrollbar {
                        width: 8px;
                        height: 8px;
                    }

                    ::-webkit-scrollbar-track {
                        background: #f1f5f9;
                        border-radius: 4px;
                    }

                    ::-webkit-scrollbar-thumb {
                        background: #cbd5e1;
                        border-radius: 4px;
                    }

                    ::-webkit-scrollbar-thumb:hover {
                        background: #94a3b8;
                    }

                    /* Estilos para mejorar la experiencia táctil en dispositivos móviles */
                    @media (hover: none) and (pointer: coarse) {
                        .p-button,
                        .p-dropdown,
                        .mobile-card {
                            min-height: 44px;
                        }
                        
                        .p-datatable .p-datatable-tbody > tr > td {
                            min-height: 44px;
                        }
                    }
                `}
            </style>
        </>
    );
};

export default Inventario;